import { useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { NoteTemplatePicker } from '@/components/NoteTemplatePicker';
import { useAddNote } from '@/api/hooks';
import type { TemplateContext } from '@/lib/note-templates';

// Cosmos writes notes at exactly two visibility levels (TicketNotes.publish):
//   1 = All Autotask Users      → the customer sees it (default)
//   2 = Internal Project Team   → internal staff only
// The "Internal" checkbox is the only control; Autotask's third level
// (4 = Internal & Co-Managed) is never written from here.
const PUBLISH_ALL = 1;
const PUBLISH_INTERNAL = 2;

/**
 * How tall the box may grow on its own before it scrolls instead. Viewport-relative so a
 * full-length template (the dark-web notification runs ~25 lines) fits without scrolling,
 * while still leaving the ticket visible behind it on a laptop screen.
 */
function maxAutoHeight(): number {
  return Math.max(240, Math.round(window.innerHeight * 0.6));
}

interface Props {
  ticketId: number;
  /** Ticket data used to fill a template's {{placeholders}} on insert. */
  templateContext?: TemplateContext;
}

export function NoteForm({ ticketId, templateContext }: Props) {
  const [description, setDescription] = useState('');
  // Always starts unchecked — notes are customer-visible unless the tech ticks Internal.
  const [internal, setInternal] = useState(false);
  // Armed by the first click on a customer-visible note; the second click posts it.
  const [confirming, setConfirming] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mutation = useAddNote();

  // Once the tech drags the resize handle their height wins — auto-grow stops fighting it
  // until the box is cleared. `dragFrom` holds the height at pointer-down so we can tell a
  // real drag from an ordinary click that happened to land near the corner.
  const manualHeight = useRef(false);
  const dragFrom = useRef<number | null>(null);

  // Grow the box to fit its content (a long inserted template is readable without dragging),
  // capped by maxAutoHeight() after which it scrolls. 'auto' first so it can shrink back.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el || manualHeight.current) return;
    el.style.height = 'auto';
    // scrollHeight is content + padding but excludes the border, which box-sizing counts in
    // height — without this the box lands ~2px short and clips the last line.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(el.scrollHeight + border, maxAutoHeight())}px`;
  }, [description]);

  // Templates are inserted, never sent: the expanded text lands in the box for editing.
  // An empty box is replaced; otherwise the template is appended after a blank line.
  const insertTemplate = (text: string, template: { isInternal: boolean }) => {
    const prefix = description.trim() ? `${description.replace(/\s+$/, '')}\n\n` : '';
    setDescription(prefix + text);
    if (template.isInternal) setInternal(true);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      // Caret at the START of what was just inserted, not the end: focus scrolls the box to
      // the caret, and the tech needs to read the note from the top and fill its placeholders.
      el.focus();
      el.setSelectionRange(prefix.length, prefix.length);
      if (!prefix) el.scrollTop = 0;
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;
    // A customer-visible note can't be unsent, so it takes one extra deliberate click.
    // Internal notes post straight away.
    if (!internal && !confirming) {
      setConfirming(true);
      return;
    }
    mutation.mutate(
      { id: ticketId, body: { description: description.trim(), publish: internal ? PUBLISH_INTERNAL : PUBLISH_ALL } },
      {
        onSuccess: () => {
          setDescription('');
          setConfirming(false);
          // Fresh box, fresh sizing — drop any height the tech dragged for the last note.
          manualHeight.current = false;
          toast.success('Note added', { description: internal ? 'Internal only' : 'Visible to the customer' });
        },
        onError: (err) => {
          setConfirming(false);
          toast.error('Failed to add note', { description: err instanceof Error ? err.message : 'Unknown error' });
        },
      },
    );
  };

  // Who the confirmation names. Falls back to the company, then a generic phrase.
  const contactName = `${templateContext?.contactFirstName ?? ''} ${templateContext?.contactLastName ?? ''}`.trim();
  const audience = contactName || templateContext?.companyName || 'the customer';

  return (
    <form
      onSubmit={submit}
      onKeyDown={(e) => { if (e.key === 'Escape' && confirming) setConfirming(false); }}
      className="space-y-2 rounded-md border bg-card p-3"
    >
      <Textarea
        ref={textareaRef}
        placeholder="Add a note…"
        value={description}
        // Editing after arming the confirmation disarms it — the text being sent has changed.
        onChange={(e) => { setDescription(e.target.value); setConfirming(false); }}
        onPointerDown={() => { dragFrom.current = textareaRef.current?.offsetHeight ?? null; }}
        onPointerUp={() => {
          const el = textareaRef.current;
          if (el && dragFrom.current != null && el.offsetHeight !== dragFrom.current) {
            manualHeight.current = true;
          }
          dragFrom.current = null;
        }}
        rows={3}
        // Vertical only — dragging a full-width box wider just overflows the card.
        className="resize-y"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <NoteTemplatePicker
            context={templateContext ?? { ticketId }}
            onInsert={insertTemplate}
          />
          <label
            htmlFor={`internal-${ticketId}`}
            className="flex cursor-pointer select-none items-center gap-2 px-2 text-xs text-muted-foreground"
          >
            <input
              id={`internal-${ticketId}`}
              type="checkbox"
              checked={internal}
              // Switching to Internal disarms the confirmation — nothing reaches the customer.
              onChange={(e) => { setInternal(e.target.checked); setConfirming(false); }}
              className="rounded"
            />
            Internal
          </label>
        </div>

        {confirming ? (
          <div className="flex items-center gap-1.5">
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" size="sm" autoFocus disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Publish to customer
            </Button>
          </div>
        ) : (
          <Button type="submit" size="sm" disabled={mutation.isPending || !description.trim()}>
            {mutation.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Add note
          </Button>
        )}
      </div>

      {confirming && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            This note will be visible to <span className="font-medium">{audience}</span>. Tick
            {' '}<span className="font-medium">Internal</span> instead to keep it to the team.
          </span>
        </div>
      )}
    </form>
  );
}
